import { Toaster } from "react-hot-toast";
import BackButtonWrapper from "../../components/button/BackButton/wrapper";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { CircleAlert, LogOutIcon, Shield, Users } from "lucide-react";
import LiveEvacuationDashboardFiltersCard from "../../components/card/LiveEvacuationDashboardFiltersCard";
import LiveEvacuationDashboardTable from "../../components/table/LiveEvacuationDashboardTable";
import { PAGE_SIZE } from "../../constants/common";
import Loader from "../../components/loader";
import { useGetEvacuationEventsParticipantsCounts } from "../../hooks/emergency-evacuation/query";
import LiveEvacuationDashboardCard from "../../components/card/LiveEvacuationDashboardCard";

const LiveEvacuationDashboard = () => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");

  const [allEventParticipants, setAllEventParticipants] = useState([]);

  const [selectedEventCode, setSelectedEventCode] = useState<string>("");
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>([]);
  const [selectedMusterPoints, setSelectedMusterPoints] = useState<number[]>(
    [],
  );

  const {
    data: evacuationEventsParticipantsCounts,
    refetch: refetchEvacuationEventsParticipantsCounts,
  } = useGetEvacuationEventsParticipantsCounts({
    s_EvacuationCode: selectedEventCode,
    n_LocationID: Number(selectedLocationId) || 0,
    n_BuildingID: Number(selectedBuildingId) || 0,
    s_EventPersonTypeCodes: selectedUserTypes.join(","),
    s_MusterPointIDs: selectedMusterPoints.join(","),
    s_EvacuationStatus: selectedStatuses.join(","),
  });

  const dashboardCardsData = useMemo(
    () => [
      {
        id: 1,
        key: 1,
        title: t("missing"),
        count: evacuationEventsParticipantsCounts?.missing || 0,
        icon: <CircleAlert className="w-5" />,
        bgColor: "bg-[#ff4d4d]",
      },
      {
        id: 2,
        key: 2,
        title: t("safe"),
        count: evacuationEventsParticipantsCounts?.safe || 0,
        icon: <Shield className="w-5" />,
        bgColor: "bg-[#47d147]",
      },
      {
        id: 3,
        key: 3,
        title: t("exited"),
        count: evacuationEventsParticipantsCounts?.exited || 0,
        icon: <LogOutIcon className="w-5" />,
        bgColor: "bg-[#4da6ff]",
      },
      {
        id: 4,
        key: 4,
        title: t("expected"),
        count: evacuationEventsParticipantsCounts?.expected || 0,
        icon: <Users className="w-5" />,
        bgColor: "bg-[#ffa31a]",
      },
    ],
    [t, evacuationEventsParticipantsCounts],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      refetchEvacuationEventsParticipantsCounts();
    }, 5000);

    return () => clearInterval(interval);
  }, [refetchEvacuationEventsParticipantsCounts]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("liveEvacuationDashboard")}
          </h1>
        </BackButtonWrapper>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {dashboardCardsData.map((item) => (
            <LiveEvacuationDashboardCard key={item.id} data={item} />
          ))}
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-3">
          <LiveEvacuationDashboardFiltersCard
            setIsLoading={setIsLoading}
            page={page}
            setPages={setPages}
            setPage={setPage}
            search={search}
            setCount={setCount}
            setAllEventParticipants={setAllEventParticipants}
            pageSize={pageSize}
            selectedEventCode={selectedEventCode}
            setSelectedEventCode={setSelectedEventCode}
            selectedLocationId={selectedLocationId}
            setSelectedLocationId={setSelectedLocationId}
            selectedBuildingId={selectedBuildingId}
            setSelectedBuildingId={setSelectedBuildingId}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            selectedUserTypes={selectedUserTypes}
            setSelectedUserTypes={setSelectedUserTypes}
            selectedMusterPoints={selectedMusterPoints}
            setSelectedMusterPoints={setSelectedMusterPoints}
          />

          {isLoading ? (
            <div className="flex justify-center lg:col-span-3">
              <Loader />
            </div>
          ) : (
            <div className="lg:col-span-3">
              <LiveEvacuationDashboardTable
                allEventParticipants={allEventParticipants}
                page={page}
                pages={pages}
                setPage={setPage}
                search={search}
                setSearch={setSearch}
                count={count}
                pageSize={pageSize}
                setPageSize={setPageSize}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LiveEvacuationDashboard;
